import { X } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoMdSettings } from 'react-icons/io';
import { LogOut } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { logout } from '../../../redux/slices/authSlice';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function MobileSidebar({
  isOpen,
  onClose,
  children,
}: MobileSidebarProps) {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const queryClient = useQueryClient();
  const handleLogout = () => {
    setIsLoggingOut(true);

    // Remove all queries from the cache
    queryClient.removeQueries();

    // Cancel any ongoing queries
    queryClient.cancelQueries();
    // Reset the query client to clear any in-memory cache
    queryClient.clear();

    dispatch(logout());
    setTimeout(() => {
      sessionStorage.removeItem('accessToken');
      navigate('/login');
    }, 500);
  };

  const SettingsIcon = IoMdSettings as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const ToDashboard = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[888] min-991:hidden ${
          isOpen
            ? 'opacity-50 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 left-0 h-full w-[280px] bg-white shadow-2xl z-[999] transform transition-transform duration-300 ease-in-out min-991:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="h-full flex flex-col">
          {/* Header */}
          <div className="px-4 py-5 flex justify-between items-center border-b border-[#E5E7EB]">
            <button onClick={ToDashboard}>
              <div className="flex items-center gap-2">
                <img
                  src="/agency/assets/workfall-logo.svg"
                  alt="workfall logo"
                  className="h-[20px]"
                />
                <span className="logo-text text-lg mt-1 text-primary">
                  Workfall
                </span>
              </div>
            </button>
            <button
              onClick={onClose}
              className="rounded-lg bg-gray-50 hover:bg-gray-100 p-1.5 transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Navigation Items */}
          <ul className="flex-1 px-2 py-4 overflow-y-auto">{children}</ul>

          {/* Footer Actions */}
          <div className="px-2 pb-4 border-t border-[#E5E7EB] pt-2">
            <Link
              to="/settings"
              onClick={onClose}
              className={`
                relative flex items-center py-2.5 px-2.5 my-1
                font-medium rounded-lg cursor-pointer
                transition-all duration-200
                ${
                  location.pathname.includes('/settings')
                    ? 'text-orange-600 bg-[#FF664633]'
                    : 'hover:bg-orange-50 text-gray-600 hover:text-orange-500'
                }
              `}
            >
              <SettingsIcon className="w-5 h-5" />
              <span className="ml-2 text-sm">Settings</span>
            </Link>

            <button
              className="flex items-center w-full py-2.5 px-2.5 my-1 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-500 transition-all duration-200"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <LogOut size={18} />
              <span className="ml-2 text-sm">
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </span>
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
}

interface MobileSidebarItemProps {
  icon: ReactNode;
  activeIcon: ReactNode;
  text: string;
  path: string;
  onClose: () => void;
  alert?: boolean;
}

export function MobileSidebarItem({
  icon,
  activeIcon,
  text,
  path,
  onClose,
  alert = false,
}: MobileSidebarItemProps) {
  const location = useLocation();
  const isActive = location.pathname.includes(path);

  return (
    <li className="relative">
      <Link
        to={path}
        onClick={onClose}
        className={`
          relative flex items-center py-2.5 px-2.5 my-1
          font-medium rounded-lg cursor-pointer
          transition-all duration-200
          ${
            isActive
              ? 'text-orange-600 bg-[#FF664633]'
              : 'hover:bg-orange-50 text-gray-600 hover:text-orange-500'
          }
        `}
      >
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {isActive ? activeIcon : icon}
        </div>

        <span className="ml-2 text-sm whitespace-nowrap">{text}</span>

        {alert && (
          <div className="absolute right-2 w-2 h-2 rounded-full bg-orange-400" />
        )}
      </Link>
    </li>
  );
}
